import { useRef, useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import { flattenToAppURL, isInternalURL } from '@plone/volto/helpers';
import { MPFButton } from 'volto-ministerio-publico-federal/components/UI';

import SlideItem from './components/SlideItem';

const resolveLink = (href = []) => {
  const link = href?.[0];
  if (!link) return null;

  if (link['@type'] !== 'Link') {
    return flattenToAppURL(link['@id']);
  }

  const remoteUrl = link.getRemoteUrl;

  return isInternalURL(remoteUrl) ? flattenToAppURL(remoteUrl) : remoteUrl;
};

const loadBannerCarousel = (() => {
  let promise;

  return () => {
    if (!promise) {
      promise = import(
        'volto-ministerio-publico-federal/theme/js/BannerTopoCarousel'
      );
    }
    return promise;
  };
})();

const HomeBannerView = ({ data }) => {
  const sliderRef = useRef(null);
  const carouselRef = useRef(null);
  const observerRef = useRef(null);
  // Pausa escolhida por quem lê, pelo botão (AB#9144). O ref existe para os
  // manipuladores de foco lerem o valor atual sem reatar nada.
  const [pausado, setPausado] = useState(false);
  const pausadoRef = useRef(false);

  const { pathname } = useLocation();

  const items = useMemo(
    () => (Array.isArray(data?.items) ? data.items : []),
    [data?.items],
  );

  const showBullets = items.length > 1;
  const delay = Number(data?.delay) || 8000;
  const autoplay = Boolean(data?.autoplay && showBullets);
  const hideMobile = Boolean(data?.hideMobile);
  // Proporção fixa (`16:5`/`16:3`, da anotação do Figma para a sub-home).
  // Vazio é o caso da capa: sem modificador, o banner segue a altura natural
  // da imagem, como sempre foi. Ver `components/_banner-topo.scss`.
  const ratioClass = ['16:5', '16:3'].includes(data?.ratio)
    ? `mpf-banner-topo--${data.ratio.replace(':', '-')}`
    : '';

  const destroyCarousel = useCallback(() => {
    carouselRef.current?.destroy();
    carouselRef.current = null;
  }, []);

  // Quem navega por teclado não pode ter o banner trocando embaixo do foco
  // (AB#9144): o `hoverpause` do Glide só cobre o ponteiro.
  const pausarAoFocar = useCallback((pausar) => {
    if (pausadoRef.current) return;
    if (pausar) carouselRef.current?.pause();
    else carouselRef.current?.play();
  }, []);

  const alternarPausa = useCallback(() => {
    const proximo = !pausadoRef.current;
    pausadoRef.current = proximo;
    setPausado(proximo);
    if (proximo) carouselRef.current?.pause();
    else carouselRef.current?.play();
  }, []);

  const initCarousel = useCallback(async () => {
    if (!sliderRef.current || carouselRef.current) return;

    const { BannerTopoCarousel } = await loadBannerCarousel();

    carouselRef.current = new BannerTopoCarousel(
      sliderRef.current,
      delay,
      autoplay,
    );

    carouselRef.current.init();
  }, [delay, autoplay]);

  useEffect(() => {
    if (!sliderRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          initCarousel();
          observer.disconnect();
        }
      },
      { threshold: 0.25 },
    );

    observer.observe(sliderRef.current);
    observerRef.current = observer;

    return () => observer.disconnect();
  }, [initCarousel]);

  useEffect(() => {
    return () => {
      // Executa sempre que o pathname mudar ou no unmount
      destroyCarousel();
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, [pathname, destroyCarousel]);

  if (items.length === 0) return null;

  return (
    <section
      className={`mpf-banner-topo ${ratioClass} ${
        hideMobile ? 'mpf-banner-topo--hide-mobile' : ''
      }`}
      aria-label="Banner institucional no topo do Portal"
      onFocusCapture={() => pausarAoFocar(true)}
      onBlurCapture={() => pausarAoFocar(false)}
    >
      <div className="mpf-banner-topo__carousel glide" ref={sliderRef}>
        <div className="glide__track" data-glide-el="track">
          <ul className="glide__slides">
            {items.map((item, index) => (
              <SlideItem
                key={item?.id || `banner-${index}`}
                item={item}
                href={resolveLink(item.href)}
              />
            ))}
          </ul>
        </div>
        {autoplay && (
          /* Fora do `controls[nav]`: o Glide liga clique em todo filho
             daquele contêiner e leria um `data-glide-dir` que este botão
             não tem. */
          <button
            type="button"
            className="mpf-banner-topo__pausa"
            aria-pressed={pausado}
            aria-label={
              pausado
                ? 'Retomar a troca automática do banner'
                : 'Pausar a troca automática do banner'
            }
            onClick={alternarPausa}
          >
            {pausado ? (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
                className="h-4 w-4"
              >
                <path d="M6 5h4v14H6zm8 0h4v14h-4z" />
              </svg>
            )}
          </button>
        )}
        {showBullets && (
          <div
            className="mpf-banner-topo__bullets"
            data-glide-el="controls[nav]"
          >
            {data.items.map((_, index) => (
              <MPFButton
                variant="unstyled"
                className="glide__bullet"
                data-glide-dir={`=${index}`}
                key={`bullet-${index}`}
                aria-label={`Ir para o slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

HomeBannerView.defaultProps = {
  data: {
    items: [],
    delay: 8000,
    autoplay: false,
    hideMobile: false,
  },
};

HomeBannerView.propTypes = {
  data: PropTypes.shape({
    items: PropTypes.array,
    delay: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    autoplay: PropTypes.bool,
    hideMobile: PropTypes.bool,
  }).isRequired,
};

export default HomeBannerView;
